import React from 'react';
import dayjs from 'dayjs';

import { getErrorMessage } from '~libs/axiosWrapper';
import { formatDateString } from '~libs/dayjs';
import { useToast } from '~libs/toast';

import { ClickableIcon, ErrorResponse, SectionCard, Spinner } from '~components';
import { FieldProps, Fields, InputProps } from '~components/form-fields';
import { faEnvelope } from '~components/icon';

import { useCreateCarePlanNotificationMutation, useGetCarePlanNotificationsQuery } from '~clients/actions/carePlans';

import { OverviewSectionProps } from './overviewSectionProps';

/**
 * Participant acknowledgement. Copy-sent date is read-only (latest notification); the
 * rights/agreement switches are editable and save with Plan Details via the shared form.
 */
const ParticipantAcknowledgementSection: React.FC<OverviewSectionProps> = ({
  plan,
  values,
  setFieldValue,
  setFieldTouched,
  touched,
  invalidFields,
}) => {
  const { showToast } = useToast();
  const { data: notifications, isLoading, error } = useGetCarePlanNotificationsQuery({ planId: plan.id });
  const [logParticipantCopy, { isLoading: isLogging }] = useCreateCarePlanNotificationMutation();

  // Chronological max, not a string sort — robust to any date format.
  const latestSentDate = (notifications ?? [])
    .map(({ notification_date }) => notification_date)
    .filter(Boolean)
    .reduce<string | undefined>((latest, date) => (!latest || dayjs(date).isAfter(latest) ? date : latest), undefined);

  // PLACEHOLDER until the real notification flow (send method + recipient) is built:
  // creates a client_care_plan_notifications record; the API fills placeholder details.
  const onLogCopySent = React.useCallback(async () => {
    try {
      await logParticipantCopy({ planId: plan.id }).unwrap();
      showToast('success', 'Participant copy logged as sent');
    } catch (caught) {
      showToast('danger', getErrorMessage(caught));
    }
  }, [logParticipantCopy, plan.id, showToast]);

  // Offer the log action only when the trail loaded and is empty. On a query error the
  // state is unknown, so don't coerce it to "not sent" and risk a duplicate audit record.
  const hasNotification = error === undefined && (notifications ?? []).length > 0;
  const canLogCopy = error === undefined && !hasNotification;

  // useCallback/useMemo so the custom cell keeps a stable identity — otherwise editing an
  // unrelated field in the shared Overview form would remount it every keystroke.
  const CopySentCell = React.useCallback<React.FC<InputProps>>(
    ({ value }) => (
      <div className="care-plan-copy-sent-row">
        <span data-cy="care-plan-copy-sent-value">{value}</span>
        {canLogCopy && (
          <ClickableIcon
            id="care-plan-log-copy-sent"
            dataCy="care-plan-log-copy-sent"
            icon={faEnvelope}
            disabled={isLogging}
            ariaLabel="Log participant copy as sent"
            toolTip="Log participant copy as sent (placeholder)"
            onClick={onLogCopySent}
          />
        )}
      </div>
    ),
    [canLogCopy, isLogging, onLogCopySent],
  );

  const definitions = React.useMemo(() => {
    const fields: FieldProps = {
      copy_sent: { type: 'custom', caption: 'Participant copy sent', component: CopySentCell, dataCy: 'care-plan-copy-sent' },
      is_rights_explained: { type: 'switch', caption: 'Statement of Rights confirmed', dataCy: 'care-plan-rights-confirmed' },
      is_service_agreement_signed: { type: 'switch', caption: 'Service agreement signed', dataCy: 'care-plan-agreement-signed' },
    };
    return Object.entries(fields).map(([key, props]) => ({ key, ...props }));
  }, [CopySentCell]);

  const copySentValue = () => {
    if (error !== undefined) return 'Unavailable';
    return latestSentDate ? formatDateString(latestSentDate) : 'Not sent';
  };

  const fieldValues = {
    copy_sent: copySentValue(),
    is_rights_explained: values.is_rights_explained,
    is_service_agreement_signed: values.is_service_agreement_signed,
  };

  return (
    <SectionCard
      title="Acknowledgements"
      titleTag="h4"
      className="care-plan-overview-section"
      dataCy="care-plan-acknowledgement"
    >
      {isLoading && (
        <div className="care-plan-section-loading">
          <Spinner loading />
        </div>
      )}
      {/* The switches are plan-driven, so render them even when the notification trail fails
          to load — only the copy-sent date is affected (shown as "Unavailable"). */}
      {!isLoading && (
        <>
          {error !== undefined && (
            <ErrorResponse dataCy="care-plan-acknowledgement-error" message={getErrorMessage(error)} />
          )}
          <Fields
            definitions={definitions}
            values={fieldValues}
            setFieldValue={setFieldValue}
            setFieldTouched={setFieldTouched}
            touched={touched}
            invalidFields={invalidFields}
          />
        </>
      )}
    </SectionCard>
  );
};

export default ParticipantAcknowledgementSection;
